import { makeStyles } from '@material-ui/styles'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import Slider from 'core/elements/Slider'
import Text from 'core/elements/Text'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import Theme from 'core/themes/model'
import { rangeValueValidator } from 'core/utils/fieldValidators'

// PCD-4129: the admin-facing range for the reserve -- below MIN_PERCENT
// there's little point reserving, above MAX_PERCENT too little capacity
// would be left to actually run workloads.
export const MIN_PERCENT = 10
export const MAX_PERCENT = 50
export const DEFAULT_PERCENT = 30

const useStyles = makeStyles<Theme>((theme) => ({
  card: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
    display: 'grid',
    gap: theme.spacing(2),
  },
  customLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  customLabelTitle: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
  sliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  slider: {
    flexGrow: 1,
  },
  percentInput: {
    maxWidth: 96,
  },
}))

interface Props {
  enabled: boolean
  onEnabledChange: (value: boolean) => void
  percent: number
  onPercentChange: (value: number) => void
  className?: string
}

// PCD-4129: lets an admin reserve a percentage of this cluster's allocated
// (overcommit-adjusted) CPU/RAM so VM-HA always has spare capacity to
// evacuate into. Saved via HaMgr.updateAllocationThreshold alongside the
// evacuation speed/VMHA config on form submit -- see EditClusterHostGroupModal.
export default function CapacityReserveCard({
  enabled,
  onEnabledChange,
  percent,
  onPercentChange,
  className,
}: Props) {
  const classes = useStyles()

  return (
    <div
      className={`${classes.card}${className ? ` ${className}` : ''}`}
      data-testid="capacity-reserve-card"
    >
      <ToggleSwitchField
        id="capacityReserveEnabled"
        label="Failover Capacity Reserve"
        value={enabled}
        onChange={onEnabledChange}
        customLabel={
          <div className={classes.customLabel}>
            <Text variant="caption1" className={classes.customLabelTitle}>
              Failover Capacity Reserve
              <HintTooltip message="Reserves a percentage of this cluster's allocated CPU and RAM capacity so VM-HA always has room to evacuate VMs after a host failure. Once placing a new VM or resize would use up the reserve, it is blocked." />
            </Text>
            <Text variant="body2">
              Keep a percentage of cluster capacity free so VM-HA can evacuate VMs after a host
              failure.
            </Text>
          </div>
        }
      />
      {enabled && (
        <div>
          <Text variant="caption1" className={classes.customLabelTitle}>
            Reserved Capacity (%)
            <HintTooltip message="Percentage of the cluster's allocated (overcommit-adjusted) CPU and RAM that must stay free. For example, 30 means the cluster may be at most 70% allocated." />
          </Text>
          <div className={classes.sliderRow}>
            <div className={classes.slider}>
              <Slider
                min={MIN_PERCENT}
                max={MAX_PERCENT}
                step={1}
                minLabel={`${MIN_PERCENT}%`}
                maxLabel={`${MAX_PERCENT}%`}
                value={percent}
                onChange={onPercentChange}
              />
            </div>
            <TextField
              id="capacityReservePercent"
              label=""
              onChange={(value) => onPercentChange(Number(value))}
              value={percent}
              type="number"
              min={MIN_PERCENT}
              max={MAX_PERCENT}
              validations={[rangeValueValidator(MIN_PERCENT, MAX_PERCENT)]}
              className={classes.percentInput}
              required
            />
          </div>
        </div>
      )}
    </div>
  )
}
